import { Plus, RefreshCw } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";

import { EventCalendar, shiftMonth } from "@/components/cinch/EventCalendar";
import { KpiTile } from "@/components/cinch/KpiTile";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCan } from "@/lib/auth";
import { cn } from "@/lib/utils";

import { useCashBuckets } from "../../cash_flow/api";
import { fmtCur } from "../../renewals/format";
import {
  useAssumptions,
  useForecastMonth,
  useSyncChurnFromRecon,
  type CalendarEvent,
  type EventKind,
} from "../api";
import { AssumptionDrawer, type DrawerTarget } from "./AssumptionDrawer";
import { AssumptionsTable } from "./AssumptionsTable";
import {
  eventEntityId,
  KIND_CLASS,
  KIND_LABEL,
  toCalendarEvent,
} from "../lib";

const ALL_KINDS: EventKind[] = [
  "payroll",
  "headcount",
  "vendor",
  "new_revenue",
  "churn",
  "one_off",
];

/** The kinds a user can create from scratch. Payroll comes from the Rippling
 *  calendar; new contracts come from the deal, not from here. */
const CREATABLE: Array<DrawerTarget["kind"]> = [
  "headcount",
  "churn",
  "vendor",
  "one_off",
];

/**
 * The Calendar tab: every dated business assumption on one month grid, with
 * the full list beneath it.
 *
 * Its organising idea is that an assumption's two dates are different facts —
 * the day the thing happens, and the day money moves. Conflating them is the
 * classic modelling error: hiring on the 12th costs nothing on the 12th, and a
 * net-60 customer's last payment arrives two months after they've gone.
 *
 * The month's own actions (add an assumption, sync churn from the close) live
 * here rather than on the page shell, so tabs added later don't inherit
 * buttons that have nothing to do with them.
 */
export function CalendarTab() {
  const now = new Date();
  const [{ year, month }, setMonth] = useState({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });
  const [active, setActive] = useState<Set<EventKind>>(new Set(ALL_KINDS));
  const [target, setTarget] = useState<DrawerTarget | null>(null);
  const [expanded, setExpanded] = useState(false);

  const canWrite = useCan("financial_forecast.write");
  const monthQuery = useForecastMonth(year, month);
  const assumptions = useAssumptions();
  const buckets = useCashBuckets();
  const syncChurn = useSyncChurnFromRecon();

  const bucketNames = useMemo(
    () => (buckets.data ?? []).filter((b) => b.kind !== "memo").map((b) => b.name),
    [buckets.data],
  );

  const shown = useMemo(
    () => (monthQuery.data?.events ?? []).filter((e) => active.has(e.kind)),
    [monthQuery.data, active],
  );
  const calendarEvents = useMemo(() => shown.map(toCalendarEvent), [shown]);
  // The calendar hands back its own event shape; this walks it back to ours.
  const byId = useMemo(() => new Map(shown.map((e) => [e.id, e])), [shown]);

  const totals = monthQuery.data?.totals;

  function toggleKind(kind: EventKind) {
    setActive((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  }

  /** Open the drawer on the row behind a calendar chip or a table row. */
  function openById(compositeId: string) {
    const { kind, entityId } = eventEntityId(compositeId);
    const data = assumptions.data;
    if (!data) return;
    if (kind === "headcount") {
      const row = data.headcount.find((r) => r.id === entityId);
      if (row) setTarget({ kind: "headcount", row });
    } else if (kind === "churn") {
      const row = data.churn.find((r) => r.id === entityId);
      if (row) setTarget({ kind: "churn", row });
    } else if (kind === "one_off") {
      const row = data.one_offs.find((r) => r.id === entityId);
      if (row) setTarget({ kind: "one_off", row });
    } else if (kind === "vendor") {
      const row = data.vendors.find((r) => r.id === entityId);
      if (row) setTarget({ kind: "vendor", row });
    } else {
      // Payroll and new contracts aren't edited here.
      toast.info(
        kind === "payroll"
          ? "Payroll comes from the Rippling pay calendar — correct it on the Cash Flow assumptions tab."
          : "New contracts are managed on the Cash Flow assumptions tab.",
      );
    }
  }

  function onSelectEvent(event: CalendarEvent) {
    if (!event.editable) {
      toast.info(
        "Payroll comes from the Rippling pay calendar — correct it on the Cash Flow assumptions tab.",
      );
      return;
    }
    openById(event.id);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-3xl text-sm text-muted-foreground">
          Every dated assumption behind the forecast — hires, churn, vendor runs,
          new contracts and one-off events. Each is shown twice: faint on the day
          it happens, solid on the day the money moves.
        </p>
        {canWrite && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                syncChurn.mutate(undefined, {
                  onSuccess: (r) =>
                    toast.success(
                      `Churn synced from the close — ${r.added} added, ${r.updated} updated${
                        r.deactivated ? `, ${r.deactivated} dropped` : ""
                      }`,
                    ),
                  onError: (e) =>
                    toast.error("Sync failed", {
                      description: (e as Error).message,
                    }),
                })
              }
              disabled={syncChurn.isPending}
              title="Pull the monthly close's settled churn dates into the forecast"
            >
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", syncChurn.isPending && "animate-spin")}
              />
              Sync churn from close
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm">
                  <Plus className="mr-1.5 h-4 w-4" /> Add assumption
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {CREATABLE.map((kind) => (
                  <DropdownMenuItem
                    key={kind}
                    onClick={() => setTarget({ kind, row: null } as DrawerTarget)}
                  >
                    {KIND_LABEL[kind as EventKind]}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>

      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))" }}
      >
        <KpiTile
          label="Cash out this month"
          value={fmtCur(totals?.outflow ?? 0)}
          sub={`${totals?.settling_count ?? 0} events settling`}
          tone="danger"
        />
        <KpiTile
          label="Cash in this month"
          value={fmtCur(totals?.inflow ?? 0)}
          tone="positive"
        />
        <KpiTile
          label="Net"
          value={fmtCur(totals?.net ?? 0)}
          tone={(totals?.net ?? 0) < 0 ? "danger" : "positive"}
        />
        <KpiTile
          label="Lands another month"
          value={String(totals?.deferred_count ?? 0)}
          sub="shown here, paid elsewhere"
        />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {ALL_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => toggleKind(kind)}
            className={cn(
              "rounded-md border px-2.5 py-1 text-xs font-medium transition-opacity",
              KIND_CLASS[kind],
              !active.has(kind) && "opacity-30",
            )}
          >
            {KIND_LABEL[kind]}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="pt-4">
          {monthQuery.error ? (
            <p className="py-8 text-center text-sm text-danger">
              {String(monthQuery.error)}
            </p>
          ) : (
            <EventCalendar
              year={year}
              month={month}
              events={calendarEvents}
              onPrev={() => setMonth(shiftMonth(year, month, -1))}
              onNext={() => setMonth(shiftMonth(year, month, 1))}
              onToday={() =>
                setMonth({
                  year: new Date().getFullYear(),
                  month: new Date().getMonth() + 1,
                })
              }
              onSelectEvent={(e) => {
                const source = byId.get(e.id);
                if (source) onSelectEvent(source);
              }}
              onSelectDay={
                canWrite
                  ? (iso) => setTarget({ kind: "one_off", row: null, date: iso })
                  : undefined
              }
              footnote={
                <>
                  A faint chip with an arrow is the day something{" "}
                  <em>happens</em>; the solid chip is the day the{" "}
                  <em>money moves</em>. A hire starting on the 12th costs
                  nothing on the 12th — it lands in the next pay run.
                  {canWrite && " Click any empty space to add an assumption on that date."}
                </>
              }
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">All assumptions</CardTitle>
          <p className="text-xs text-muted-foreground">
            Sorted by when the money moves. Reading down the two date columns
            shows how far apart “it happened” and “we paid for it” really are.
          </p>
        </CardHeader>
        <CardContent>
          <AssumptionsTable
            data={assumptions.data}
            loading={assumptions.isLoading}
            error={assumptions.error}
            filterKinds={active}
            onSelect={openById}
          />
        </CardContent>
      </Card>

      <AssumptionDrawer
        target={target}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onClose={() => {
          setTarget(null);
          setExpanded(false);
        }}
        bucketNames={bucketNames}
      />
    </div>
  );
}
